import SDK from "casdoor-js-sdk";
import { Config } from "./config";
import { v4 as uuidv4 } from "uuid";

const signinRedirectPath = "/auth/signin-callback";

const publicRoutes = [
  "signature_capture",
  "gb_form",
  "paymentportal",
  "newclient",
  "auth/suspended",
];

let casdoor: SDK;

// Broadcast channel to communicate with other tabs
const authChannel = new BroadcastChannel("auth");
const events = {
  tokenRefreshed: "token-refreshed",
  loggedOut: "logged-out",
};

// const refreshBeforeExpiration = 58 * 60 * 1000; // 58 minutes before expiration (3 minutes after obtaining the token)
const refreshBeforeExpiration = 60 * 1000; // 60 seconds before expiration

// Timeout id for the token refresh
let refreshTokenTimeoutId;

// Lock name for token refresh
const tokenRefreshLockName = "token-refresh-lock";

const getAuth = () => {
  const storageAuth = localStorage.getItem("auth");
  return storageAuth ? JSON.parse(storageAuth) : null;
};

const getIdentity = () => {
  const storageIdentity = localStorage.getItem("identity");
  return storageIdentity ? JSON.parse(storageIdentity) : null;
};

const getVisitorFingerprintId = () => localStorage.getItem("fid");

const getAccessToken = () => {
  const auth = getAuth();
  return auth?.access_token || "";
};

const getAuthorizationHeader = () => {
  const auth = getAuth();
  if (!auth) return "";
  return `${auth.token_type} ${auth.access_token}`;
};

const isPublicRoute = () => {
  return !!publicRoutes.find(
    (i) => window.location.pathname.toLowerCase().indexOf(i) >= 0
  );
};

function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const milliseconds = date.getMilliseconds().toString().padStart(3, "0");

  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

// Listen for messages from the auth channel
authChannel.onmessage = (event) => {
  if (event.data.type === events.tokenRefreshed) {
    console.log(`${formatTime(new Date())} - Token refreshed by another tab`);
    restoreAuthFromStorage();
  }
  if (event.data.type === events.loggedOut) {
    console.log(`${formatTime(new Date())} - Logged out by another tab`);
    clear();
  }
};
// Initialize the auth service
const init = async (config: Config) => {
  casdoor = new SDK({
    serverUrl: config.casdoor.serverUrl,
    clientId: config.casdoor.clientId,
    organizationName: config.casdoor.organizationName,
    appName: config.casdoor.appName,
    redirectPath: signinRedirectPath,
    storage: window.localStorage,
  });

  if (isPublicRoute()) {
    return;
  }

  // Generate unique visitor id if it doesn't already exist
  if (!getVisitorFingerprintId()) {
    const newFingerprintId = uuidv4();
    localStorage.setItem("fid", newFingerprintId);
  }

  // This is to handle the case where the user is redirected back to the app after signing in.
  // Exchange the code for the access token if the current path is the redirect path.
  // Then, update the visitor fingerprint id on the server.
  if (window.location.pathname === signinRedirectPath) {
    try {
      const result = await casdoor.exchangeForAccessToken();
      await setNewAuth(result);
      await fetch(`${config.getBaseURL()}/api/update-visitor-fingerprint`, {
        method: "POST",
        headers: {
          Authorization: getAuthorizationHeader(),
          "Visitor-Fingerprint-Id": getVisitorFingerprintId(),
        },
      });
      authChannel.postMessage({ type: events.tokenRefreshed });
      window.location.href = "/";
      return;
    } catch (error) {
      console.log(
        `${formatTime(
          new Date()
        )} - Failed to exchange code for an access token or update fingerprint`,
        error
      );
      await logout();
      return;
    }
  }

  await restoreAuthFromStorage();
};

// Redirect to the sign-in page
const signInRedirect = async () => {
  await casdoor.signin_redirect();
};

// Restore auth info from local storage
const restoreAuthFromStorage = async () => {
  console.log(
    `${formatTime(new Date())} - Restoring auth and identity from local storage`
  );

  await scheduleTokenRefresh();
};

// Set the new auth info and update the identity info.
// Schedule token refresh after setting the new auth info.
const setNewAuth = async (val) => {
  console.log(`${formatTime(new Date())} - Setting new auth info`);

  localStorage.setItem("auth", JSON.stringify(val));

  const { payload } = casdoor.parseAccessToken(val.access_token);
  localStorage.setItem("identity", JSON.stringify(payload));

  await scheduleTokenRefresh();
};

// Schedule token refresh based on the expiration time of the access token.
// Clear the previous timeout if it exists.
// If the access token is expired, refresh it using the refresh token.
const scheduleTokenRefresh = async () => {
  console.log(`${formatTime(new Date())} - Scheduling token refresh`);

  if (refreshTokenTimeoutId) {
    clearTimeout(refreshTokenTimeoutId);
  }

  // Convert expiration time from seconds to milliseconds
  const identity = getIdentity();
  const expiresAt = (identity?.exp || 0) * 1000;
  const currentTime = Date.now();

  const refreshIn = expiresAt - currentTime - refreshBeforeExpiration;

  if (refreshIn <= 0) {
    console.log(
      `${formatTime(
        new Date()
      )} - Access token already expired, trying to refresh the token`
    );
    await refreshAccessToken();
    return;
  }

  refreshTokenTimeoutId = setTimeout(async () => {
    await refreshAccessToken();
  }, refreshIn);
  console.log(
    `${formatTime(
      new Date()
    )} - Scheduled token refresh in ${refreshIn} milliseconds`
  );
};

// Check if the access token is not expired
const isAccessTokenValid = () => {
  const identity = getIdentity();

  // Convert expiration time from seconds to milliseconds
  const expiresAt = (identity?.exp || 0) * 1000;
  const currentTime = Date.now();

  // Check if token is expired or will expire in the "refreshBefore" seconds
  const isValid = currentTime < expiresAt - refreshBeforeExpiration;

  console.log(
    `${formatTime(new Date())} - Token expiration check: ${
      isValid ? "valid" : "expired or expiring soon"
    }`
  );
  return isValid;
};

// Refresh the access token using the refresh token and update the auth info.
// If the refresh token is invalid, log the user out.
// Retry 10 times with a 3-second interval on fail.
// If all 10 retries were unsuccessful, log out the user.
// Use navigator.locks to prevent multiple refresh token requests in parallel.
// Broadcast token refreshed event after the token is refreshed.
// If shouldRetry option explicitly is set to false then only try to refresh token once
// without any timeouts (used when gateway returns 401 error).
const refreshAccessToken = async (skipRetry = false) => {
  console.log(`${formatTime(new Date())} - Refreshing access token`);

  try {
    await navigator.locks.request(tokenRefreshLockName, async (lock) => {
      console.log(
        `${formatTime(new Date())} - Acquired lock for token refresh`
      );

      if (isAccessTokenValid()) {
        console.log(
          `${formatTime(new Date())} - Access token is valid, skipping refresh`
        );
        return;
      }

      if (skipRetry) {
        try {
          const auth = getAuth();
          if (!auth?.refresh_token) {
            throw new Error("No refresh token found");
          }

          const result = await casdoor.refreshAccessToken(auth.refresh_token);

          if (!result.access_token) {
            throw new Error("Failed to refresh access token");
          }

          await setNewAuth(result);
          authChannel.postMessage({ type: events.tokenRefreshed });
          console.log(
            `${formatTime(
              new Date()
            )} - Token refreshed successfully in non-retry mode`
          );
          window.location.reload();
        } catch (error) {
          console.log(
            `${formatTime(new Date())} - Failed to refresh access token`,
            error
          );
          await logout();
        }
      } else {
        let retryCount = 0;
        while (retryCount < 10) {
          try {
            if (isAccessTokenValid()) {
              console.log(
                `${formatTime(
                  new Date()
                )} - Access token is valid, skipping refresh`
              );
              return;
            }

            const auth = getAuth();
            if (!auth?.refresh_token) {
              console.log(`${formatTime(new Date())} - No refresh token found`);
              return;
            }

            const result = await casdoor.refreshAccessToken(auth.refresh_token);

            if (!result.access_token) {
              console.log(
                `${formatTime(new Date())} - Failed to refresh access token`
              );
              throw new Error("Failed to refresh access token");
            }

            await setNewAuth(result);
            authChannel.postMessage({ type: events.tokenRefreshed });
            console.log(
              `${formatTime(
                new Date()
              )} - Token refreshed successfully in retry mode`
            );

            return;
          } catch (error) {
            console.log(
              `${formatTime(
                new Date()
              )} - Failed to refresh access token, retrying in 2 seconds...`
            );
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }
      }
    });
  } catch (error) {
    console.log(
      `${formatTime(new Date())} - Failed to acquire lock for token refresh`,
      error
    );
  }
};

// Log out the user by removing the auth info from local storage.
const logout = async () => {
  console.log(`${formatTime(new Date())} - Logging out`);

  clear();

  authChannel.postMessage({ type: events.loggedOut });

  await signInRedirect();
};

// Handle the case where the user's fingerprint is suspended.
const handleSuspendedFingerprint = async () => {
  console.log(`${formatTime(new Date())} - Fingerprint suspended`);

  window.dispatchEvent(new CustomEvent("fingerprint-suspended"));
};

const clear = () => {
  console.log(`${formatTime(new Date())} - Clearing auth info`);

  localStorage.removeItem("auth");
  localStorage.removeItem("identity");

  if (refreshTokenTimeoutId) {
    clearTimeout(refreshTokenTimeoutId);
  }
};

export const authService = {
  getAuth,
  getIdentity,
  getVisitorFingerprintId,
  getAccessToken,
  getAuthorizationHeader,
  isPublicRoute,
  init,
  signInRedirect,
  refreshAccessToken,
  logout,
  handleSuspendedFingerprint,
};
